import { getDataFromTree } from '@apollo/client/react/ssr';
import * as Sentry from '@sentry/nextjs';
// Types
import { NextPage } from 'next';
import { StoreStatus } from 'api/graphql-global-types';
// Api
import { GET_STORE_BY_SLUG } from 'api/store/queries';
import {
  GetStoreBySlug,
  GetStoreBySlugVariables,
} from 'api/store/types/GetStoreBySlug';
// Helpers
import {
  ApolloPageContext,
  PageProps,
  getCleanQueryParam,
  markPageAsNotFound,
} from 'helpers/next-page';
// Hocks
import withApollo from 'hocs/withApollo';
// Hooks
import { useRedirectOnError, useAthletePageData } from 'hooks';
// Layouts
import StripeElements from 'layouts/StripeElements/StripeElements';
// Components
import AthleteContainer from 'components/Athlete/AthleteContainer/AthleteContainer';
import NotFoundPage from 'components/NotFoundPage/NotFoundPage';

const AthletePageContent = (): JSX.Element => {
  const { error, store, loading } = useAthletePageData();

  const isAthletePending = store?.status === StoreStatus.Pending;

  useRedirectOnError({ error, redirectTo: '/', skipTokenError: true });

  return (
    <StripeElements>
      <AthleteContainer
        store={store}
        isPending={isAthletePending}
        padding="default"
        loading={loading}
      />
    </StripeElements>
  );
};

const Athlete: NextPage<PageProps> = ({ isNotFound }) => {
  if (isNotFound) {
    return <NotFoundPage />;
  }

  return <AthletePageContent />;
};

Athlete.getInitialProps = async (
  ctx: ApolloPageContext
): Promise<PageProps> => {
  const athleteSlug = getCleanQueryParam(ctx.query.athleteSlug);

  if (!ctx.apolloClient || !athleteSlug) {
    return {};
  }

  try {
    const { data } = await ctx.apolloClient.query<
      GetStoreBySlug,
      GetStoreBySlugVariables
    >({
      query: GET_STORE_BY_SLUG,
      variables: {
        input: {
          slug: athleteSlug,
        },
        withFollowerCount: false,
      },
    });

    if (!data?.store) {
      markPageAsNotFound(ctx);
      return { isNotFound: true };
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: { section: 'ssr', page: '[athleteSlug]' },
    });
    markPageAsNotFound(ctx);
    return { isNotFound: true };
  }

  return {};
};

export default withApollo(Athlete, { getDataFromTree });
